import React, { useRef } from "react";
import "./Table.scss";

import Loader from "../Loader";
import Pagination from "./Pagination";

export interface Column {
  label?: string;
  header?: (column: Column) => React.ReactNode;
  accessor: string;
  align?: "left" | "center" | "right";
  width?: number;
  render?: (value: any, row: any, rowIndex: number) => React.ReactNode;
}

interface Props {
  data: Array<Record<string, any>>;
  columns: Array<Column>;
  showPagination?: boolean;
  totalRecords?: number; // Total records for pagination
  pageSize?: number; // How many records per page
  currentPage?: number; // Current page
  onPageChange?: (page: number) => void; // Callback for page change
  onPageSizeChange?: (pageSize: number) => void; // Callback for page size change
  isLoading?: boolean; // Is data being fetched
  border?: boolean;
  errorMessage?: string;
  rowKey?: string;
  onScroll?: (scrollData: {
    scrollLeft: number;
    scrollWidth: number;
    clientWidth: number;
  }) => void;
  onRowClick?: (row: any) => void;
}

const Table: React.FC<Props> = ({
  data,
  columns,
  showPagination = false,
  totalRecords = 0,
  pageSize = 10,
  currentPage = 1,
  onPageChange = () => {},
  onPageSizeChange = () => {},
  isLoading = false,
  border = false,
  onScroll,
  errorMessage = "",
  rowKey = "id",
  onRowClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (containerRef.current && onScroll) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      onScroll({ scrollLeft, scrollWidth, clientWidth });
    }
  };

  return (
    <div
      className="table-container-custom"
      ref={containerRef}
      onScroll={handleScroll}
    >
      <table className="table" border={border ? 1 : 0}>
        <thead>
          <tr>
            {columns.map((col, index) => (
              <th
                key={index}
                style={{
                  textAlign: col.align || "left",
                  width: col.width ? `${col.width}px` : "auto",
                }}
              >
                {col.header ? col.header(col) : col.label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {data?.length > 0 ? (
            data.map((rowData, rowIndex) => (
              <tr
                key={rowData[rowKey] ?? rowData.patient_id ?? rowIndex}
                onClick={() => onRowClick?.(rowData)}
              >
                {columns.map((col, colIndex) => (
                  <td
                    key={colIndex}
                    style={{
                      textAlign: col.align || "left",
                      width: col.width ? `${col.width}px` : "auto",
                    }}
                  >
                    {col.render
                      ? col.render(
                          rowData[col.accessor],
                          {
                            index: rowIndex,
                            ...rowData,
                          },
                          rowIndex,
                        )
                      : rowData[col.accessor] || "-"}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="no-data">
                {errorMessage || "No data available"}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination Controls */}
      {showPagination && (
        <Pagination
          totalRecords={totalRecords}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          dataLength={data?.length}
        />
      )}

      {/* Loader overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <Loader noBackground />
        </div>
      )}
    </div>
  );
};

export default Table;
